import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RequestOptions, TApiResponse, TErrorMessage, TSuccess } from "@/types";
import {
    getInspectionDetails,
    getInspections,
    createInspection,
    updateInspection,
    deleteInspection
} from "@/features/Inspections/services/index";


import toast from "react-hot-toast";

export const QUERY_KEY = "inspections";

export function useGetInspections(options: RequestOptions) {
    const {isLoading, isFetching, data} = useQuery({
        queryKey: [QUERY_KEY, options.search, {...options.pager, ...options.filter}],
        queryFn: () => getInspections(options),
    });

    return { isLoading, isFetching, inspections: data };
}

export function useGetInspectionDetails(inspectionId: string) {
    const {isLoading, isFetching, data} = useQuery({
        queryKey: [QUERY_KEY, inspectionId],
        queryFn: () => getInspectionDetails(inspectionId),
        enabled: !!inspectionId
    });

    return { isLoading, isFetching, inspection: data };
}

export function useInspectionCreate(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createInspection,
        onSuccess: (res) => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]});
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    });
}

export function useInspectionEdit(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({inspectionId, data}: { inspectionId: string; data: any }) =>
            updateInspection(inspectionId, data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]});
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    });
}

export function useInspectionDelete(
    onSuccess?: TSuccess<TApiResponse<null>>
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteInspection,
        onSuccess: (res) => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEY]});
            onSuccess && onSuccess(res);
        },
        onError: (error: TErrorMessage) => {
            toast.error(error.response?.data.message ?? "");
        },
    });
}